import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import flatten from 'lodash/flatten';

import SortableTable from 'components/sortable-table/SortableTable';
import PopoverScrollable from 'components/popover-scrollable/PopoverScrollable';
import Tooltip from 'components/Tooltip';
import AddNew from 'components/buttons/add-new/AddNew';
import Spinner from 'components/spinner/Spinner';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';

import { withModal } from 'components/modal/ModalHOC';

import SOVModalGeneric from 'modules/insights/sov/components/modals/generic/SOVModalGeneric';
import SOVQueriesModal from 'modules/insights/sov/components/queries/modals/SOVQueriesModal';

import { resetMergedQueries, updateMergedQueries } from 'modules/insights/sov/reducers/queries/sov-queries';

import { __t, applyTranslations } from 'utils/language';
import { insertIf } from 'utils/list';
import { CREATE, DELETE, DUPLICATE, EDIT, FIXED_TO_TOP, QUERY } from 'modules/insights/sov/utils/sov';
import { CHANNELS, MULTI, convertLocalQueryToEndpoint, mergeQueries } from 'modules/insights/sov/utils/queries/sov-queries';
import { CONSUMER_INSIGHTS } from 'modules/insights/sov/utils/consumer-insights/sov-consumer-insights';
import {
  getColumnChannels,
  getColumnCheckbox,
  getColumnHashtags,
  getColumnIcons,
  getColumnKeyword,
  getColumnMentions,
  getColumnTags
} from 'modules/insights/sov/utils/tables/sov-tables';
import {
  ICONS_MAP,
  enrichQueryIcon,
  getIconDelete,
  getIconDuplicate,
  getIconEdit
} from 'modules/insights/sov/utils/icons/sov-icons';

import './sov-queries-consumer-insights-instagram.scss';

import type { SVGDynamicIconProps, SortableTableColumnType } from 'types';
import type {
  SOVGeneralProps,
  SOVIconEnriched,
  SOVQueryEndpoint,
  SOVQueryEndpointMulti,
  SOVQueryKeywordsList,
  SOVQueryLoading,
  SOVQueryLocal,
  SOVQuerySave,
  SOVReport
} from 'modules/insights/sov/types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('modules/insights/sov/shared/sov-shared.i18n.json'));
applyTranslations(require('modules/insights/sov/components/queries/sov-queries.i18n.json'));

type OwnProps = {
  report: SOVReport,
  isActionEvent: boolean,
  onUpdateReport: (a: SOVGeneralProps) => void,
  onSaveQuery: (a: SOVQuerySave) => void,
  onUpdateQuery: (b: SOVQueryEndpoint, a: boolean) => void,
  onDuplicateQuery: (a: SOVGeneralProps) => void,
  onDeleteQuery: (a: SOVGeneralProps) => void
};

type StateProps = {
  queriesSingle: Array<SOVQueryEndpoint>,
  queriesMulti: Array<SOVQueryEndpointMulti>,
  queriesMerged: Array<SOVQueryLocal>,
  isLoadingQueries: SOVQueryLoading
};

type DispatchProps = {
  dispatchUpdateMergedQueries: (a: Array<Partial<SOVQueryLocal>>) => Promise<void>,
  dispatchResetMergedQueries: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

const AddNewWithQueriesModal = withModal(AddNew, {
  modalContent: SOVQueriesModal,
  width: 'small',
  positionOverride: FIXED_TO_TOP
});

const IconWithQueriesModal = withModal(Tooltip, {
  modalContent: SOVQueriesModal,
  width: 'small',
  positionOverride: FIXED_TO_TOP
});

const IconWithGenericModal = withModal(Tooltip, {
  modalContent: SOVModalGeneric,
  width: 'small',
  positionOverride: FIXED_TO_TOP
});

const SHARED_MSG_KEY = 'sov_shared';
const MSG_KEY = 'sov_queries';

class SOVQueriesConsumerInsightsInstagram extends React.PureComponent<Props> {
  componentDidUpdate() {
    const { queriesSingle, queriesMulti, queriesMerged, dispatchUpdateMergedQueries } = this.props;
    const mergedQueries = mergeQueries(queriesSingle, queriesMulti);

    if (!isEqual(queriesMerged, mergedQueries)) {
      dispatchUpdateMergedQueries(mergedQueries);
    }
  }

  componentWillUnmount() {
    const { dispatchResetMergedQueries } = this.props;

    dispatchResetMergedQueries();
  }

  getIconEdit(query: SOVQueryLocal): Partial<SOVIconEnriched> {
    const { report } = this.props;
    const icon = getIconEdit();

    return enrichQueryIcon(query, report, icon);
  }

  getIconDuplicate(query: SOVQueryLocal): Partial<SOVIconEnriched> {
    const { report, onDuplicateQuery } = this.props;
    const icon = getIconDuplicate(onDuplicateQuery);

    return enrichQueryIcon(query, report, icon);
  }

  getIconDelete(query: SOVQueryLocal): Partial<SOVIconEnriched> {
    const { report, onDeleteQuery } = this.props;
    const icon = getIconDelete(onDeleteQuery);

    return enrichQueryIcon(query, report, icon);
  }

  getModalTitle(type: string): string {
    return __t(`${MSG_KEY}.labels.${type}`);
  }

  getModalLabels(type: string): SOVGeneralProps {
    return {
      actionButton: __t(`${MSG_KEY}.buttons.${type}`)
    };
  }

  getColumnKeywordsList(query: SOVQueryLocal, isMulti: boolean, columnType: string): SOVQueryKeywordsList {
    const endpointQuery = convertLocalQueryToEndpoint(query);
    let included = !isMulti ? endpointQuery.includedKeywords : [];
    let custom = !isMulti ? endpointQuery.customKeywords : [];

    if (isMulti) {
      const primary = endpointQuery.queries ? endpointQuery.queries[0] : {};
      const secondary = endpointQuery.queries ? endpointQuery.queries[1] : {};

      included = (primary.includedKeywords || []).concat(secondary.includedKeywords || []);
      custom = (primary.customKeywords || []).concat(secondary.customKeywords || []);
    }

    included = included?.filter(item => item.type === columnType);
    custom = custom?.filter(item => item.type === columnType && item.selected);

    return {
      included: included ?? [],
      custom: custom ?? []
    };
  }

  getColumnChannelsList(query: SOVQueryLocal, isMulti: boolean): string {
    const endpointQuery = convertLocalQueryToEndpoint(query);
    let channels = !isMulti ? endpointQuery.channels : [];

    if (isMulti) {
      const primary = endpointQuery.queries ? endpointQuery.queries[0] : {};
      const secondary = endpointQuery.queries ? endpointQuery.queries[1] : {};

      channels = (primary.channels || []).concat(secondary.channels || []);
    }

    return channels?.filter(item => item.keyword.length > 0)
      .map(item => item.keyword)
      .join('; ') || '';
  }

  getColumns(): Array<SortableTableColumnType> {
    const { queriesMerged } = this.props;

    return [
      ...insertIf(!isEmpty(queriesMerged), ...[
        getColumnCheckbox(this.handleToggleQuery, 'sov-queries-consumer-insights-instagram__column-status'),
        getColumnKeyword(MSG_KEY, 'sov-queries-consumer-insights-instagram__column-keyword'),
        getColumnHashtags(this.renderColumnTitle, this.renderColumnText),
        getColumnMentions(this.renderColumnTitle, this.renderColumnText),
        getColumnTags(this.renderColumnTitle, this.renderColumnText),
        getColumnChannels(this.renderColumnTitle, this.renderChannelColumnText, 'sov-queries-consumer-insights-instagram__channels'),
        getColumnIcons(this.renderIconModal, 'sov-queries-consumer-insights-instagram__icons')
      ])
    ];
  }

  getQueryData(): Array<SOVQueryLocal> {
    const { queriesMerged } = this.props;

    return queriesMerged.map(query => ({
      ...query,
      icons: [
        this.getIconEdit(query),
        this.getIconDuplicate(query),
        this.getIconDelete(query)
      ]
    } as SOVQueryLocal));
  }

  handleToggleQuery = (event: SyntheticEvent<HTMLInputElement>, query: SOVQueryLocal): void => {
    const { onUpdateQuery } = this.props;
    const endpointQuery = convertLocalQueryToEndpoint(query);

    const updatedQuery = {
      ...endpointQuery,
      enabled: !endpointQuery.enabled
    } as SOVQueryEndpoint;

    onUpdateQuery(updatedQuery, query.type === MULTI);
  };

  handleToggleChannels = (event: SyntheticEvent<HTMLInputElement>): void => {
    const { onUpdateReport } = this.props;
    const target = event.currentTarget;

    onUpdateReport({ channelsInclude: target.checked });
  };

  renderColumnChannelsCheckbox(): React.ReactNode {
    const { report } = this.props;
    const isChecked = report.channelsInclude;

    return (
      <InputCheckbox
        id="sov-queries-consumer-insights-instagram__column-channels-checkbox"
        name="sov-queries-consumer-insights-instagram__column-channels-checkbox"
        value={String(isChecked)}
        checked={isChecked}
        onChange={this.handleToggleChannels}
      />
    );
  }

  renderColumnTitle = (columnName: string): JSX.Element => {
    const isChannelsColumn = columnName === CHANNELS;

    return (
      <div className="sov-queries-consumer-insights-instagram__column-title">
        {
          isChannelsColumn &&
          this.renderColumnChannelsCheckbox()
        }
        <div>
          {__t(`${MSG_KEY}.labels.included_${columnName}`)}
        </div>
        <Tooltip
          message={__t(`${SHARED_MSG_KEY}.tooltips.${columnName}`)}
          width="long"
          textAlign="left"
          classNameWrapper="sov-queries-consumer-insights-instagram__column-title-tooltip"
          showQuestionMark
        />
      </div>
    );
  };

  renderColumnText = (query: SOVQueryLocal, columnType: string): JSX.Element => {
    const isMulti = query.type === MULTI;
    const list = this.getColumnKeywordsList(query, isMulti, columnType);

    const keywords = [
      list.custom
        .map(k => k.keyword),
      list.included
        .map(k => k.keyword)
    ]
      .filter(text => text.length > 0);

    return (
      <PopoverScrollable
        withFixedVerticalScroll
        identifier={columnType}
        size="small"
        classNameTrigger="text-overflow-ellipsis"
        disabled={!query.enabled}
        list={flatten(keywords).map(item => ({
          text: item
        }))}
        trigger={(
          <React.Fragment>
            {keywords.join('; ')}
          </React.Fragment>
        )}
      />
    );
  };

  renderChannelColumnText = (query: SOVQueryLocal): JSX.Element => {
    const { report } = this.props;
    const isMulti = query.type === MULTI;
    const list = this.getColumnChannelsList(query, isMulti);

    return (
      <PopoverScrollable
        withFixedVerticalScroll
        identifier="channels"
        size="small"
        classNameTrigger="text-overflow-ellipsis"
        disabled={!query.enabled || !report.channelsInclude}
        list={list.split('; ').map(item => ({
          text: item
        }))}
        trigger={(
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <React.Fragment>
            {list}
          </React.Fragment>
        )}
      />
    );
  };

  renderIconModal = (icon: SOVIconEnriched, iconProps: SVGDynamicIconProps): React.ReactNode | undefined | null => {
    if (icon.name === ICONS_MAP.edit) {
      return this.renderModalEdit(icon, iconProps);
    }

    if (icon.name === ICONS_MAP.duplicate) {
      return this.renderModalDuplicate(icon, iconProps);
    }

    if (icon.name === ICONS_MAP.delete) {
      return this.renderModalDelete(icon, iconProps);
    }

    return null;
  };

  renderModalEdit(icon: SOVIconEnriched, iconProps: SVGDynamicIconProps): React.ReactNode {
    const { report, onSaveQuery } = this.props;

    return (
      <IconWithQueriesModal
        message={icon.tooltip}
        width="micro"
        modalContentProps={{
          report,
          query: icon.query,
          pageType: CONSUMER_INSIGHTS,
          sectionType: QUERY,
          actionType: EDIT,
          onSaveQuery
        }}
      >
        <SVGDynamicIcon {...iconProps} />
      </IconWithQueriesModal>
    );
  }

  renderModalDuplicate(icon: SOVIconEnriched, iconProps: SVGDynamicIconProps): React.ReactNode {
    return (
      <IconWithGenericModal
        message={icon.tooltip}
        width="micro"
        modalContentProps={{
          title: this.getModalTitle(DUPLICATE),
          labels: this.getModalLabels(DUPLICATE),
          message: __t(`${MSG_KEY}.messages.${DUPLICATE}`, {
            queryName: icon.query.name || ''
          }),
          report: icon.report,
          reportType: icon.report.reportType,
          query: icon.query,
          sectionType: QUERY,
          actionType: DUPLICATE,
          onAction: icon.onDuplicate
        }}
      >
        <SVGDynamicIcon {...iconProps} />
      </IconWithGenericModal>
    );
  }

  renderModalDelete(icon: SOVIconEnriched, iconProps: SVGDynamicIconProps): React.ReactNode {
    return (
      <IconWithGenericModal
        message={icon.tooltip}
        width="micro"
        modalContentProps={{
          title: this.getModalTitle(DELETE),
          labels: this.getModalLabels(DELETE),
          message: __t(`${MSG_KEY}.messages.${DELETE}`, {
            queryName: icon.query.name || ''
          }),
          report: icon.report,
          reportType: icon.report.reportType,
          query: icon.query,
          sectionType: QUERY,
          actionType: DELETE,
          onAction: icon.onDelete
        }}
      >
        <SVGDynamicIcon {...iconProps} />
      </IconWithGenericModal>
    );
  }

  renderSpinner(): React.ReactNode {
    return (
      <div className="sov-queries-consumer-insights-instagram__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  renderAddNew(): React.ReactNode {
    const { report, onSaveQuery } = this.props;

    return (
      <div className="sov-queries-consumer-insights-instagram__add-new">
        <AddNewWithQueriesModal
          text={__t(`${SHARED_MSG_KEY}.labels.add_new`)}
          tooltip={__t(`${MSG_KEY}.tooltips.add_new`)}
          modalContentProps={{
            report,
            query: {},
            pageType: CONSUMER_INSIGHTS,
            sectionType: QUERY,
            actionType: CREATE,
            onSaveQuery
          }}
          icon={{
            name: 'plus-inside-circle-custom-bg-white-fg',
            width: 16,
            height: 16
          }}
        />
      </div>
    );
  }

  renderHeader(): React.ReactNode {
    return (
      <div className="sov-queries-consumer-insights-instagram__header">
        <div className="sov-queries-consumer-insights-instagram__header-text">
          {__t(`${MSG_KEY}.labels.queries`)}
        </div>
        {this.renderAddNew()}
      </div>
    );
  }

  renderTableOverlay(): React.ReactNode {
    return (
      <div className="sov-queries-consumer-insights-instagram__table-overlay">
        {this.renderSpinner()}
      </div>
    );
  }

  renderTable(): React.ReactNode {
    const { isLoadingQueries, isActionEvent } = this.props;
    const isLoading = isLoadingQueries.single || isLoadingQueries.multi;
    const queryData = this.getQueryData();

    return (
      <React.Fragment>
        {
          isLoading && isActionEvent &&
            this.renderTableOverlay()
        }
        <SortableTable
          data={queryData}
          columns={this.getColumns()}
          layout="simple"
          maxHeight={286}
        />
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const { isLoadingQueries, isActionEvent } = this.props;
    const isLoading = isLoadingQueries.single || isLoadingQueries.multi;

    return (
      <div className="sov-queries-consumer-insights-instagram">
        <div className="sov-queries-consumer-insights-instagram__list">
          {this.renderHeader()}
          <div className="sov-queries-consumer-insights-instagram__table">
            {
              isLoading && !isActionEvent
                ? this.renderSpinner()
                : this.renderTable()
            }
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (
  {
    sovQueries,
    sovQueriesSingleConsumerInsights,
    sovQueriesMultiConsumerInsights
  }
): StateProps => ({
  queriesSingle: sovQueriesSingleConsumerInsights.queries,
  queriesMulti: sovQueriesMultiConsumerInsights.queries,
  queriesMerged: sovQueries.queriesMerged,

  isLoadingQueries: {
    single: sovQueriesSingleConsumerInsights.isLoading,
    multi: sovQueriesMultiConsumerInsights.isLoading
  }
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchUpdateMergedQueries: (mergedQueries: Array<Partial<SOVQueryLocal>>) => (
    dispatch(updateMergedQueries(mergedQueries))
  ),

  dispatchResetMergedQueries: () => (
    dispatch(resetMergedQueries())
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(SOVQueriesConsumerInsightsInstagram);

